<template>
  <NuxtLayout main-class="layout-main" name="sticky">
    <template #header>
      <ScratchcardMyOrdersHeader
        v-model:display-mode="mode"
        :color="order?.visualParameters.titleColor"
        :show-menu="order && isRevealed(order.state)"
        :title="order?.gameName"
        @left-button-click="
          navigateBack({
            previousRoute: $localePath('/my-orders/scratchcards'),
          })
        "
      />
    </template>

    <section
      v-if="order && notCancelledScratchTickets && notCancelledScratchTickets.length > 0"
      class="slide-show-wrapper content-wrap flex flex-col"
    >
      <AnimationPlayer
        v-if="isScratchAllLoading"
        class="animation-player"
        name="scratch-all"
        @ready="animationPlayer = $event"
      >
        <template #reduced-motion>
          <Illustration name="static-scratchcard-loop" />
        </template>
      </AnimationPlayer>
      <ScratchcardSlideShow
        v-bind="{ mode }"
        class="slideshow"
        :class="{ 'slideshow-hide': isScratchAllLoading }"
        :state="order.state"
        :tickets="notCancelledScratchTickets"
        :winnings="order.winningsPayload ?? null"
        @click:details="openInfoDetailsModal"
        @click:inprogress="openInfoInProgressModal"
        @click:ticket="handleTicketClick"
        @click:zoom="openFullScreenImageModal"
      />
    </section>

    <section class="content-wrap flex flex-col gap-300 px-100 mb-300">
      <div v-if="isScratchable" class="px-200">
        <LcButton
          class="w-full"
          :count="order?.quantityActive"
          data-testid="scratchcard-scratch-all-button"
          :loading="isScratchAllLoading"
          :loading-text="$t('myorders.detail.scratchcard.cta.scratchallcards.loadingtext')"
          :long-press-delay="2_500"
          :long-press-hint-delay="1_300"
          size="medium"
          trigger="long-press"
          :variant="isScratchAllLoading ? 'tertiary' : 'filled'"
          @longpress="invokeRevealAll"
        >
          {{ $t('myorders.detail.scratchcard.cta.scratchallcards') }}
        </LcButton>
      </div>

      <ScratchcardOrderInformationCard v-bind="{ loading, ...order }" />
    </section>

    <LazyScratchcardWinningModal
      v-if="
        winningAnimationModal.show &&
        (winningAnimationModal.winnings || winningAnimationModal.freeTickets)
      "
      :free-tickets="winningAnimationModal.freeTickets"
      :is-big-win="winningAnimationModal.isBigWin"
      :prize="winningAnimationModal.winnings"
      @close="winningAnimationModal.show = false"
    />

    <LazyScratchcardImageModal
      v-if="ticketModal.show"
      v-bind="ticketModal.props"
      @close="ticketModal.show = false"
    />

    <LazyScratchcardInfoModalDetails
      v-if="infoDetailsModal.show"
      v-bind="infoDetailsModal.props"
      @close="infoDetailsModal.show = false"
    />

    <LazyScratchcardInfoModalInProgress
      v-if="infoInProgressModal.show"
      v-bind="infoInProgressModal.props"
      @close="infoInProgressModal.show = false"
    />

    <template v-if="isScratchable" #navbar>
      <LcButton class="w-full" size="medium" @click="() => navigateToScratchMode()">
        {{ $t('myorders.detail.scratchcard.cta.scratchnow') }}
      </LcButton>
    </template>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import type {
  ImagePropertiesPayload,
  Money,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import type { AnimationPlayer } from '~/components/Animation/AnimationPlayer.vue'

import { MODE_LOOKUP } from '#scratchcard/app/components/constants'
import {
  SCRATCHCARD_DISPLAY_MODES,
  type ScratchcardDisplayMode,
} from '#scratchcard/app/components/types'
import { byNotCancelled } from '#scratchcard/app/utils/scratchcardOrder'

type AnimationModal = {
  freeTickets: number
  isBigWin: boolean
  show: boolean
  winnings: Maybe<Money>
}

definePageMeta({
  footer: false,
  layout: false,
  middleware: ['auth', 'scratchcard-order'],
  scrollToTop: true,
  tracking: { name: 'MyOrdersScratchcard' },
})

const { currentRoute, replace } = useRouter()
const { clearScratchcardOrdersCache } = useApolloCache()
const { navigateBack, navigateToLocalized } = useRouterUtils()
const { toUrl } = useClientUrl()
const { log } = useDatadog()

const { loading, onResult, refetch, result } = useScratchcardOrderQuery(
  { orderId: currentRoute.value.params.id },
  { fetchPolicy: 'cache-only' },
)

const { loading: isRevealAllLoading, mutate: revealAll } = useRevealScratchcardOrderMutation()

const mode = ref<ScratchcardDisplayMode>(SCRATCHCARD_DISPLAY_MODES.FRONT_SCRATCHED)

const expiredAtTimeout = ref<NodeJS.Timeout | null>(null)
const animationPlayer = ref<AnimationPlayer>()
const isScratchAllLoading = ref(false)

const infoDetailsModal = reactive({
  props: {
    orderedDate: '',
    processedDate: '',
    serialNumber: '',
  },
  show: false,
})

const infoInProgressModal = reactive({
  props: { serialNumber: '' },
  show: false,
})

const ticketModal = reactive({
  props: { name: '', properties: {} as ImagePropertiesPayload, src: '' },
  show: false,
})

const winningAnimationModal = reactive<AnimationModal>({
  freeTickets: 0,
  isBigWin: false,
  show: false,
  winnings: null,
})

const isScratchable = computed(() => order.value?.state && isRevealing(order.value.state))

const notCancelledScratchTickets = computed(() => order.value?.scratchcards.filter(byNotCancelled))

const order = computed(() => result.value?.scratchcardOrder)

const getTicket = (serialNumber: string) =>
  order.value?.scratchcards.find((ticket) => ticket.serialNumber === serialNumber)

const handleTicketClick = (serialNumber: string) => {
  const ticket = getTicket(serialNumber)
  if (!ticket || !isRevealing(ticket.status)) return

  navigateToScratchMode(serialNumber)
}

const invokeRevealAll = async () => {
  if (isRevealAllLoading.value) return

  isScratchAllLoading.value = true
  animationPlayer.value?.play()

  const minAnimationTime = 2800
  const startTime = Date.now()

  try {
    const result = await revealAll({
      input: { orderId: String(currentRoute.value.params.id) },
    })
    const alreadyRevealedSerialNumbers =
      order.value?.scratchcards.filter(isScratchcardRevealed).map(toSerialNumber) ?? []

    const newRevealedScratchcards =
      result?.data?.revealOrder.scratchcards.filter(
        ({ serialNumber }) => !alreadyRevealedSerialNumbers.includes(serialNumber),
      ) ?? []

    const possibleWinnings = newRevealedScratchcards.sort(byWin)[0]?.winningsPayload

    const isBigWin = newRevealedScratchcards.some((ticket) => ticket.winningsPayload?.isBigWin)

    const totalWinningsAmount = newRevealedScratchcards.reduce(
      (acc, ticket) => acc + (ticket.winningsPayload?.amount?.value ?? 0),
      0,
    )

    const freeTickets = newRevealedScratchcards.reduce(
      (acc, ticket) => acc + (ticket.winningsPayload?.freeTickets ?? 0),
      0,
    )

    const elapsed = Date.now() - startTime
    if (elapsed < minAnimationTime) {
      await new Promise((resolve) => setTimeout(resolve, minAnimationTime - elapsed))
    }

    if (possibleWinnings) {
      const { amount } = possibleWinnings
      winningAnimationModal.isBigWin = isBigWin
      winningAnimationModal.winnings = amount ? { ...amount, value: totalWinningsAmount } : null
      winningAnimationModal.freeTickets = freeTickets ?? null
      winningAnimationModal.show = true
    }

    animationPlayer.value?.stop()
    isScratchAllLoading.value = false

    await refetch()
    clearScratchcardOrdersCache(true)
  } catch (error) {
    log('error', {
      component: 'pages/my-orders/scratchcard/[id]/index.vue',
      error,
      message: 'Failed to reveal all scratchcards',
    })
  }
}

const navigateToScratchMode = (serialNumber?: string) => {
  const path = `/my-orders/scratchcard/${currentRoute.value.params.id}/scratching`
  navigateToLocalized(serialNumber ? `${path}/${serialNumber}` : path)
}

const openInfoDetailsModal = (serialNumber: string) => {
  const ticket = getTicket(serialNumber)
  if (!ticket) return

  infoDetailsModal.props = {
    orderedDate: order.value?.orderedDate ?? '',
    processedDate: ticket.processedDate ?? '',
    serialNumber: ticket.serialNumber,
  }

  infoDetailsModal.show = true
}

const openFullScreenImageModal = (serialNumber: string) => {
  const images = getTicket(serialNumber)?.images
  if (!images) return

  const { imageField, propertiesField } = MODE_LOOKUP[mode.value]
  ticketModal.props = {
    name: imageField,
    properties: images[propertiesField],
    src: toUrl(images[imageField]),
  }

  ticketModal.show = true
}

const openInfoInProgressModal = (serialNumber: string) => {
  const ticket = getTicket(serialNumber)
  if (!ticket) return

  infoInProgressModal.props = {
    serialNumber: ticket.serialNumber,
  }
  infoInProgressModal.show = true
}

onResult((result) => {
  const firstImage = result.data.scratchcardOrder.scratchcards?.[0]?.images
  if (!firstImage) return
  const linkeExpiresAt = new Date(firstImage.linkExpiresAt).getTime()
  const currentDate = new Date().getTime()

  expiredAtTimeout.value = setTimeout(refetch, Math.floor(linkeExpiresAt - currentDate))
})

onMounted(async () => {
  const query = currentRoute.value.query
  if (query.scratch || query.serialNumber) {
    const { scratch, serialNumber, ...rest } = query
    await replace({ query: rest })
  }
})

onBeforeUnmount(() => {
  if (!expiredAtTimeout.value) return
  clearTimeout(expiredAtTimeout.value)
})
</script>

<style lang="scss" scoped>
:deep(.layout-main) {
  max-width: 100%;
  padding: token('size/space/gap/none');
}

.slide-show-wrapper {
  position: relative;
  margin-top: -1.25rem;

  .animation-player {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 17.5rem;
    transform: translate(-50%, -50%);
  }

  .slideshow {
    transition: opacity 0.3s ease-in-out;
  }

  .slideshow-hide {
    opacity: 0;
  }
}
</style>
